import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useParams } from "react-router-dom";
import Select from "react-select";
import { Button, Card, Col, Form, Row, CloseButton } from "react-bootstrap";
import useAPI from '../../../hooks/useAPI';
import useToast from "../../../hooks/useToast";
import useUserContext from '../../../hooks/useUserContext';
import Required from "../../../components/Required";
import BackBtn from '../../../components/UI/BackBtn';
import { isAdmin, isModerator, isStratAdmin, isStratCurator } from '../../../services/rolesSvc';
import ForbiddenAccess from '../../../components/ForrbiddenAccessComponent/ForbiddenAccess';
import TooltippedElement from '../../../components/common/TooltippedElement';
import { MonetaryFloatInput } from '../../../components/common/MonetaryFloatInput';

let idCounter = 1;

const OwnFundsEditReceipt = () => {
  const api = useAPI();
  const userCtx = useUserContext();
	const { policyId, receiptId } = useParams();
  const history = useHistory();
  const addToast = useToast();

  const [type, setType] = useState('');
  const [descr, setDescr] = useState('');
  const [docProps, setDocProps] = useState('');
  const [sum, setSum] = useState('');
  const [year, setYear] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentProjName, setCurrentProjName] = useState('');
	const [projectsOptions, setProjectsOptions] = useState([]);

  const [documentsList, setDocumentsList] = useState([{
    docId: idCounter,
    docName: '',
    docNum: '',
    docDate: '',
    costItem: '',
    sum: '',
    internalFundSum: '',
    comment: '',
  }]);

  const canEdit = useMemo(() => isAdmin(userCtx) || isModerator(userCtx) || isStratAdmin(userCtx, 4) || isStratCurator(userCtx, 4), [userCtx])

  const handleAddDoc = () => {
    idCounter++;
    setDocumentsList((state) => {
      return [
        ...state,
        {
          docId: idCounter,
          docName: '',
          docNum: '',
          docDate: '',
          costItem: '',
          sum: '',
          internalFundSum: '',
          comment: '',
        },
      ]
    })
  };

  const handleCloseDoc = (id, docId) => {
    const updatedDocumentsList = documentsList.filter((doc) => doc.docId !== Number(docId) && Number(doc.id) !== Number(id));
    setDocumentsList(updatedDocumentsList);
  };

  const handleChangeDoc = (id, docId, field, value) => {
    const changingDoc = documentsList.find((doc) => doc.docId === Number(docId) || Number(doc.id) === Number(id));
    changingDoc[field] = value;

    const newDocsList = documentsList.filter((doc) => doc.docId !== Number(docId) && Number(doc.id) !== Number(id));

    setDocumentsList(_.orderBy([ ...newDocsList, changingDoc ], ['id', 'docId'], ['asc', 'asc']));
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    // const actualYear = year ? { year } : {};

    const data = {
      // ...actualYear,
      year: new Date().getFullYear(),
      id: Number(receiptId),
      type: type,
      docNum: docProps,
      descr: descr,
      sum: sum,
      incomes: documentsList,
      strategyId: selectedProject,
    };

    const result = await api.post(`/factdata/internalSciFunds/${receiptId}`, data);

    if (!result.hasOwnProperty('error')) {
      addToast.success('Запись о собственных средствах сохранена');
      history.push(`/policies/${policyId}/budget?t=nioktrOwnFunds`);
    }
  };

  const getData = useCallback(async () => {
    try {
      const result = await api.get(`/factdata/internalscifunds/${receiptId}`);

      setCurrentProjName(result.parentName);
      setSum(result.sum);
      setYear(result.year);
      setType(result.type);
      setDescr(result.descr);
      setDocProps(result.docNum);
      setDocumentsList(result.incomes);
      setSelectedProject(result.strategyId);
    } catch (error) {
      console.log(`Ошибка при загрузке данных с сервера: ${error}`)
    }
  }, [receiptId]);

  useEffect(() => {
    const getProjectOptions = async() => {
      const activeProjects = await api.get('/mng/strategies?active=true');
		  setProjectsOptions(activeProjects.map((strategy) => ({label: strategy.name, value: strategy.id})));
    }
    getProjectOptions();
    if (Boolean(Number(receiptId))) {
      getData();
    }
  }, [receiptId]);

  const renderDocumentsCards = () => {
    return documentsList?.map((doc) => (
      <Card key={doc.id || doc.docId}>
        <Card.Header className='d-flex justify-content-end'>
          <CloseButton onClick={() => handleCloseDoc(doc.id, doc.docId)}/>
        </Card.Header>

        <Card.Body>
          <Row className='mt-3'>
            <Form.Group as={Col} md={4}>
              <Form.Label style={styledLabel}>Наименование документа <Required/></Form.Label>
              <Form.Control
                name={'docName'}
                value={doc.docName}
                required
                onChange={({target}) => handleChangeDoc(doc.id, doc.docId, 'docName', target.value)}
              />
            </Form.Group>

            <Form.Group as={Col} md={2}>
              <Form.Label style={styledLabel}>Номер <Required/></Form.Label>
              <Form.Control
                name={'docNum'}
                value={doc.docNum}
                required
                onChange={({target}) => handleChangeDoc(doc.id, doc.docId, 'docNum', target.value)}
              />
            </Form.Group>

            <Form.Group as={Col} md={2}>
              <Form.Label style={styledLabel}>Дата <Required/></Form.Label>
              <Form.Control
                name={'docDate'}
                value={doc.docDate.split('T')[0]}
                required
                type="date"
                onChange={({target}) => handleChangeDoc(doc.id, doc.docId, 'docDate', target.value)}
              />
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <Form.Label style={styledLabel}>Статьи расходов <Required/></Form.Label>
              <Form.Control
                name={'costItem'}
                value={doc.costItem}
                required
                onChange={({target}) => handleChangeDoc(doc.id, doc.docId, 'costItem', target.value)}
              />
            </Form.Group>
          </Row>

          <Row className='mt-4'>
            <Form.Group as={Col} md={4}>
              <Form.Label style={styledLabel}>Сумма всего <Required/></Form.Label>
              <MonetaryFloatInput
                name={'sum'}
                value={doc.sum}
                required
                onChange={(value) => handleChangeDoc(doc.id, doc.docId, 'sum', value)}
              />
            </Form.Group>
          </Row>

          <Form.Group as={Col} className='mt-4'>
            <Form.Label style={styledLabel}>В том числе, сумма за счет собственных средств, направленных на проведение прикладных научных исследований и (или) экспериментальных разработок <Required/></Form.Label>
            <MonetaryFloatInput
              name={'internalFundSum'}
              value={doc.internalFundSum}
              required
              onChange={(value) => handleChangeDoc(doc.id, doc.docId, 'internalFundSum', value)}
            />
          </Form.Group>

          <Form.Group as={Col} className='mt-4'>
            <Form.Label style={styledLabel}>Примечание</Form.Label>
            <Form.Control
              name={'comment'}
              value={doc.comment}
              onChange={({target}) => handleChangeDoc(doc.id, doc.docId, 'comment', target.value)}
            />
          </Form.Group>
        </Card.Body>
      </Card>
    ))
  };

  return (
    <section className="page project-page">
      <div className="container">
        <BackBtn />

        {canEdit ? (
          <Form onSubmit={handleSubmit}>
            <h3 style={styledHeader}>Заполнение карточки по собственным средствам, направленным на НИОКТР</h3>

            <Card style={{marginBottom: '20px'}}>
              <Card.Body style={{padding: '25px', display: 'flex', flexDirection: 'column', rowGap: '24px'}}>
                <div style={{ fontWeight: 600, color: '#212529' }}>Описание (наименование) доходов в части, направленной на мероприятия Программы развития университета (при необходимости с указанием элементов затрат)</div>

                <Form.Group as={Col}>
                  <Form.Label style={styledLabel}>
                    Вид собственных внебюджетных доходов
                    {' '}
                    <Required/>
                    <TooltippedElement iconSize="16px" tooltip="Не указываются доходы, указанные во вкладке «Поступления от НИОКТР»" />
                  </Form.Label>
                  <Form.Control
                    name={'type'}
                    value={type}
                    required
                    onChange={({target}) => setType(target.value)}
                  />
                </Form.Group>

                <Form.Group as={Col}>
                  <Form.Label style={styledLabel}>Реквизиты первичных документов, подтверждающие получение доходов <Required/></Form.Label>
                  <Form.Control
                    name={'docProps'}
                    value={docProps}
                    required
                    onChange={({target}) => setDocProps(target.value)}
                    placeholder='Введите реквизиты в формате №… от дд.мм.гггг'
                  />
                </Form.Group>

                <Form.Group as={Col}>
                  <Form.Label style={styledLabel}>Краткая характеристика доходов</Form.Label>
                  <Form.Control
                    name={'descr'}
                    value={descr}
                    onChange={({target}) => setDescr(target.value)}
                  />
                </Form.Group>

                <Form.Group as={Col}>
                  <Form.Label style={styledLabel}>Сумма, в части, направленной на мероприятия Программы развития университета, руб. <Required/></Form.Label>
                  <MonetaryFloatInput
                    name={'sum'}
                    value={sum}
                    required
                    onChange={setSum}
                  />
                </Form.Group>

                <Form.Group as={Col}>
                  <Form.Label style={styledLabel}>Стратегический проект или раздел научно-исследовательской политики Программы развития университета, в рамках реализации которого (ых) поступили денежные средства</Form.Label>
                  <Select
                    value={projectsOptions.find(opt => opt.value === selectedProject)}
                    options={projectsOptions}
                    onChange={(opt) => setSelectedProject(opt.value)}
                    noOptionsMessage={() => 'Список пуст'}
                    placeholder={'Выберите из списка'}
                    isSearchable={false}
                    isDisabled={Boolean(currentProjName)}
                  />
                </Form.Group>

                <div style={{ fontWeight: 600, color: '#212529' }}>Платежное поручение или иной документ, подтверждающий выплату денежных средств (кассовый расход) по направлению стратегического проекта или раздела научно-исследовательской политики Программы развития университета, в рамках реализации которого (ых) поступили денежные средства</div>

                {renderDocumentsCards()}

                <Button
                  onClick={handleAddDoc}
                  type={'button'}
                  variant={'outline-primary'}
                  as={Col}
                  xs={6}
                  md={4}
                  lg={3}
                  xl={2}
                  className='mt-1'
                >
                  + Добавить 
                </Button>
              </Card.Body>
            </Card>

            <Card>
              <Card.Body style={{display: 'flex', columnGap: '8px', padding: '30px 25px'}}>
                <Button 
                  onClick={() => {
                    new Promise(resolve => {
                      resolve(history.goBack())
                    })
                  }}
                  type={'button'} 
                  variant={'outline-secondary'}
                >
                  Отменить
                </Button>
                <Button type='submit'>Сохранить</Button>
              </Card.Body>
            </Card>
          </Form>
        ) :
        <ForbiddenAccess/>}
      </div>
    </section>
  );
};

export default OwnFundsEditReceipt;

const styledHeader = {
  color: '#212529',
  margin: '20px 0 36px',
  textAlign: 'start',
  fontWeight: 600,
};

const styledLabel = {
  fontWeight: 600,
  color: '#7F7F7F',
};
